import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Spinner,
  Box,
  BlockStack,
  InlineStack
} from '@shopify/polaris';
import { DebugPanel } from './DebugPanel';

interface KatanaConfig {
  apiKey: string;
  apiUrl: string;
  isConnected: boolean;
}

export default function KatanaSettings() {
  const [katanaConfig, setKatanaConfig] = useState<KatanaConfig>({
    apiKey: '',
    apiUrl: 'https://api.katanamrp.com',
    isConnected: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; content: string } | null>(null);

  // Load existing configuration
  useEffect(() => {
    loadKatanaConfig();
  }, []);

  const loadKatanaConfig = async () => {
    try {
      setLoading(true);
      
      // Get shop parameter using the SAME logic as katana service
      const getShopParameter = (): string => {
        // Get shop from URL parameters - Shopify passes this automatically
        const urlParams = new URLSearchParams(window.location.search);
        const shopFromUrl = urlParams.get('shop');
        
        if (shopFromUrl && shopFromUrl.endsWith('.myshopify.com')) {
          console.log('✅ Settings - Shop from URL:', shopFromUrl);
          return shopFromUrl;
        }
        
        // Try to get shop domain from localStorage FIRST (persists across page reloads)
        const storedShop = localStorage.getItem('shopify_shop_domain');
        if (storedShop && storedShop.endsWith('.myshopify.com') && storedShop !== 'dev-shop.myshopify.com') {
          console.log('✅ Settings - Shop from localStorage:', storedShop);
          return storedShop;
        }
        
        // Fallback to global config set by App Bridge initialization
        const globalWindow = window as any;
        if (globalWindow.shopifyUrlParams?.shop && 
            globalWindow.shopifyUrlParams.shop !== 'dev-shop.myshopify.com') {
          console.log('✅ Settings - Shop from global config:', globalWindow.shopifyUrlParams.shop);
          return globalWindow.shopifyUrlParams.shop;
        }
        
        // Development fallback
        console.log('🔧 Settings - Development mode - no shop parameter');
        return 'dev-shop.myshopify.com';
      };
      
      const shop = getShopParameter();
      
      const response = await fetch(`/api/katana/settings?shop=${shop}`);
             if (response.ok) {
         const config = await response.json() as KatanaConfig;
         setKatanaConfig(config);
       } else {
        setMessage({ type: 'info', content: 'No Katana configuration found. Please set up your API key.' });
      }
    } catch (error) {
      console.error('Error loading Katana config:', error);
      setMessage({ type: 'error', content: 'Failed to load Katana configuration.' });
    } finally {
      setLoading(false);
    }
  };

  const saveKatanaConfig = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      // Get shop parameter using the SAME logic as katana service
      const getShopParameter = (): string => {
        const urlParams = new URLSearchParams(window.location.search);
        const shopFromUrl = urlParams.get('shop');
        
        if (shopFromUrl && shopFromUrl.endsWith('.myshopify.com')) {
          return shopFromUrl;
        }
        
        // Try to get shop domain from localStorage FIRST (persists across page reloads)
        const storedShop = localStorage.getItem('shopify_shop_domain');
        if (storedShop && storedShop.endsWith('.myshopify.com') && storedShop !== 'dev-shop.myshopify.com') {
          return storedShop;
        }
        
        const globalWindow = window as any;
        if (globalWindow.shopifyUrlParams?.shop && 
            globalWindow.shopifyUrlParams.shop !== 'dev-shop.myshopify.com') {
          return globalWindow.shopifyUrlParams.shop;
        }
        
        return 'dev-shop.myshopify.com';
      };
      
      const shop = getShopParameter();
      
      const response = await fetch(`/api/katana/settings?shop=${shop}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(katanaConfig)
      });

             if (response.ok) {
         const updatedConfig = await response.json() as KatanaConfig;
         setKatanaConfig(updatedConfig);
         setMessage({ type: 'success', content: 'Katana configuration saved successfully!' });
       } else {
         const errorData = await response.json() as { error?: string };
         setMessage({ type: 'error', content: errorData.error || 'Failed to save configuration.' });
       }
    } catch (error) {
      console.error('Error saving Katana config:', error);
      setMessage({ type: 'error', content: 'Failed to save Katana configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setMessage(null);
      
      // Get shop parameter using the SAME logic as katana service
      const getShopParameter = (): string => {
        const urlParams = new URLSearchParams(window.location.search);
        const shopFromUrl = urlParams.get('shop');
        
        if (shopFromUrl && shopFromUrl.endsWith('.myshopify.com')) {
          return shopFromUrl;
        }
        
        // Try to get shop domain from localStorage FIRST (persists across page reloads)
        const storedShop = localStorage.getItem('shopify_shop_domain');
        if (storedShop && storedShop.endsWith('.myshopify.com') && storedShop !== 'dev-shop.myshopify.com') {
          return storedShop;
        }
        
        const globalWindow = window as any;
        if (globalWindow.shopifyUrlParams?.shop && 
            globalWindow.shopifyUrlParams.shop !== 'dev-shop.myshopify.com') {
          return globalWindow.shopifyUrlParams.shop;
        }
        
        return 'dev-shop.myshopify.com';
      };
      
      const shop = getShopParameter();
      
      const response = await fetch(`/api/katana/test-connection?shop=${shop}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: katanaConfig.apiKey,
          apiUrl: katanaConfig.apiUrl
        })
      });

             const result = await response.json() as { success?: boolean; error?: string };
       
       if (response.ok && result.success) {
         setKatanaConfig(prev => ({ ...prev, isConnected: true }));
         setMessage({ type: 'success', content: 'Katana connection successful! The app will now use real data.' });
         
         // Reload the configuration to get updated connection status from backend
         await loadKatanaConfig();
         
         // Clear any cached data and trigger a data refresh without full page reload
         // This preserves the current app state while switching to real data
         console.log('✅ Connection successful - clearing cache and switching to real data');
         
         // Force refresh shop detection to ensure we use the correct shop parameter
         if ((window as any).katanaService) {
           (window as any).katanaService.refreshShopDetection();
           console.log('🔄 Shop detection cache cleared');
         }
       } else {
         setKatanaConfig(prev => ({ ...prev, isConnected: false }));
         setMessage({ type: 'error', content: result.error || 'Connection test failed.' });
       }
    } catch (error) {
      console.error('Error testing connection:', error);
      setMessage({ type: 'error', content: 'Failed to test connection.' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <>
        {/* Page header card for development mode */}
        <Card>
          <Box padding="400">
            <BlockStack gap="200">
              <Text as="h1" variant="headingLg">Katana MRP Settings</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Configure your Katana MRP integration
              </Text>
            </BlockStack>
          </Box>
        </Card>
        
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <BlockStack gap="400" align="center">
                  <Spinner size="large" />
                  <Text as="p" variant="bodyMd">
                    Loading settings...
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </>
    );
  }

  return (
    <>
      {/* Page header card for development mode */}
      <Card>
        <Box padding="400">
          <BlockStack gap="200">
            <Text as="h1" variant="headingLg">Katana MRP Settings</Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Configure your Katana MRP integration
            </Text>
          </BlockStack>
        </Box>
      </Card>
      
      <Layout>
        <Layout.Section>
          {message && (
            <Banner
              tone={message.type === 'success' ? 'success' : message.type === 'error' ? 'critical' : 'info'}
              onDismiss={() => setMessage(null)}
            >
              {message.content}
            </Banner>
          )}
          
          <Card>
            <FormLayout>
              <TextField
                label="API Key"
                value={katanaConfig.apiKey}
                onChange={(value) => setKatanaConfig(prev => ({ ...prev, apiKey: value }))}
                type="password"
                autoComplete="off"
                helpText="Your Katana MRP API key. You can find this in your Katana account settings."
              />
              
              <TextField
                label="API URL"
                value={katanaConfig.apiUrl}
                onChange={(value) => setKatanaConfig(prev => ({ ...prev, apiUrl: value }))}
                helpText="The base URL for your Katana MRP API. Usually https://api.katanamrp.com"
                autoComplete="off"
              />
              
              <Box paddingBlockStart="400">
                <InlineStack gap="300">
                  <Button
                    variant="primary"
                    onClick={saveKatanaConfig}
                    loading={saving}
                  >
                    Save Configuration
                  </Button>
                  <Button
                    onClick={testConnection}
                    loading={testing}
                    disabled={!katanaConfig.apiKey}
                  >
                    Test Connection
                  </Button>
                </InlineStack>
              </Box>
            </FormLayout>
          </Card>
        </Layout.Section>
        
        <Layout.Section variant="oneThird">
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Connection Status</Text>
                <BlockStack gap="200">
                  <Text as="dt" variant="bodyMd" fontWeight="medium">
                    Status
                  </Text>
                  <Text as="dd" variant="bodyMd" tone={katanaConfig.isConnected ? 'success' : 'critical'}>
                    {katanaConfig.isConnected ? '✅ Connected' : '❌ Not Connected'}
                  </Text>
                  
                  {katanaConfig.isConnected && (
                    <>
                      <Text as="dt" variant="bodyMd" fontWeight="medium">
                        API URL
                      </Text>
                      <Text as="dd" variant="bodyMd" tone="subdued">
                        {katanaConfig.apiUrl}
                      </Text>
                    </>
                  )}
                </BlockStack>
              </BlockStack>
            </Box>
          </Card>
          
          <DebugPanel />
        </Layout.Section>
      </Layout>
    </>
  );
}
